/**
 * WooCommerce Module
 * WooCommerce specific enhancements
 */

const WooCommerce = {
    init() {
        this.initAjaxCart();
        this.initQuantityButtons();
        this.initQuickView();
        this.initWooCommerceMessages();
        this.initCartFragmentRefresh();
    },

    /**
     * Handle cart fragment refresh
     */
    initCartFragmentRefresh() {
        document.addEventListener('wc_fragment_refresh', () => {
            this.updateCartFragments();
        });

        document.addEventListener('wc_cart_updated', () => {
            this.updateCartFragments();
        });
    },

    updateCartFragments() {
        fetch(cm_ajax.ajax_url + '?action=cm_get_cart_fragments', {
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(response => response.json())
        .then(data => {
            if (data.success && data.data.fragments) {
                Object.keys(data.data.fragments).forEach((key) => {
                    const element = document.querySelector(key);
                    if (element) {
                        element.innerHTML = data.data.fragments[key];
                    }
                });
            }
        })
        .catch(error => {
            console.error('Error updating cart fragments:', error);
        });
    },

    /**
     * Intercept WooCommerce default messages and convert to toast
     */
    initWooCommerceMessages() {
        document.addEventListener('DOMContentLoaded', () => {
            const noticesWrapper = document.querySelector('.woocommerce-notices-wrapper');
            
            if (noticesWrapper) {
                const observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                const message = node.querySelector('.woocommerce-message, .woocommerce-error, .woocommerce-info');
                                if (message && !message.dataset.toastConverted) {
                                    message.dataset.toastConverted = 'true';
                                    this.convertWooMessageToToast(message);
                                }
                            }
                        });
                    });
                });
                
                observer.observe(noticesWrapper, { 
                    childList: true,
                    subtree: true 
                });
            }

            document.body.addEventListener('added_to_cart', (event) => {
                const fragments = event.fragments;
                const cartHash = event.cart_hash;
                
                if (fragments) {
                    Object.keys(fragments).forEach((key) => {
                        const element = document.querySelector(key);
                        if (element) {
                            element.innerHTML = fragments[key];
                        }
                    });
                }
            });
        });
    },

    convertWooMessageToToast(messageEl) {
        let messageText = messageEl.textContent.trim();
        messageText = messageText.replace(/Warenenkorb anzeigen$/, '').trim();
        
        const isError = messageEl.classList.contains('woocommerce-error');
        const isInfo = messageEl.classList.contains('woocommerce-info');
        
        let type = 'success';
        if (isError) type = 'error';
        else if (isInfo) type = 'info';
        
        const hasCartLink = messageEl.querySelector('.wc-forward');
        const cartUrl = hasCartLink ? hasCartLink.href : null;
        
        window.CMToast.show(messageText, type, {
            showCartLink: hasCartLink !== null,
            cartUrl: cartUrl
        });
        
        setTimeout(() => {
            messageEl.remove();
        }, 100);
    },

    /**
     * AJAX Add to Cart
     */
    initAjaxCart() {
        document.addEventListener('click', (e) => {
            const button = e.target.closest('.ajax-add-to-cart');
            if (!button) return;

            e.preventDefault();
            
            const productId = button.dataset.productId;
            const quantity = button.dataset.quantity || 1;

            this.addToCart(productId, quantity, button);
        });
    },

    addToCart(productId, quantity, button) {
        button.classList.add('is-loading');
        button.disabled = true;

        const formData = new FormData();
        formData.append('action', 'cm_add_to_cart');
        formData.append('product_id', productId);
        formData.append('quantity', quantity);

        fetch(cm_ajax.ajax_url, {
            method: 'POST',
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                this.updateCartCount(data.data.cart_count);
                this.showNotification('Produkt wurde zum Warenkorb hinzugefügt', 'success');
                
                // Update mini cart
                document.dispatchEvent(new CustomEvent('wc_fragment_refresh'));
            } else {
                this.showNotification(data.data.message || 'Fehler beim Hinzufügen', 'error');
            }
        })
        .catch(error => {
            console.error('Error:', error);
            this.showNotification('Ein Fehler ist aufgetreten', 'error');
        })
        .finally(() => {
            button.classList.remove('is-loading');
            button.disabled = false;
        });
    },

    /**
     * Quantity Buttons
     */
    initQuantityButtons() {
        document.addEventListener('click', (e) => {
            const button = e.target.closest('.qty-button');
            if (!button) return;

            const input = button.parentElement.querySelector('.qty');
            const currentVal = parseInt(input.value) || 0;
            const step = parseInt(input.step) || 1;
            const min = parseInt(input.min) || 0;
            const max = parseInt(input.max) || 999;

            if (button.classList.contains('qty-plus')) {
                if (currentVal < max) {
                    input.value = currentVal + step;
                }
            } else if (button.classList.contains('qty-minus')) {
                if (currentVal > min) {
                    input.value = currentVal - step;
                }
            }

            // Trigger change event
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    },

    /**
     * Quick View
     */
    initQuickView() {
        document.addEventListener('click', (e) => {
            const button = e.target.closest('.quick-view-button');
            if (!button) return;

            e.preventDefault();
            const productId = button.dataset.productId;
            this.loadQuickView(productId);
        });
    },

    loadQuickView(productId) {
        if (window.CMModal) {
            window.CMModal.open('quick-view-modal');
        }

        const modalContent = document.querySelector('#quick-view-modal .modal__body');
        if (modalContent) {
            modalContent.innerHTML = '<div class="loading">Laden...</div>';

            fetch(`${cm_ajax.ajax_url}?action=cm_quick_view&product_id=${productId}`)
                .then(response => response.text())
                .then(html => {
                    modalContent.innerHTML = html;
                })
                .catch(error => {
                    modalContent.innerHTML = '<p class="error">Fehler beim Laden</p>';
                });
        }
    },

    /**
     * Update cart count
     */
    updateCartCount(count) {
        const counters = document.querySelectorAll('.cart-count');
        counters.forEach(counter => {
            counter.textContent = count;
            counter.classList.add('is-updated');
            setTimeout(() => counter.classList.remove('is-updated'), 300);
        });
    },

    /**
     * Show notification
     */
    showNotification(message, type = 'info') {
        window.CMToast.show(message, type);
    }
};

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    if (document.body.classList.contains('woocommerce')) {
        WooCommerce.init();
    }
});

export default WooCommerce;
